import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { listDemands, type Demand } from '../lib/demands-api.js';
import { DomainApiError } from '../lib/api-error.js';
import { demandSummary } from '../lib/demand-stages.js';
import { useT } from '../lib/i18n.js';

/**
 * Záložka „Poptávky" v detailu zakázky (krok CRM1, bod 4): zájemci o danou
 * nemovitost. Filtr je `propertyId` — poptávka se k nemovitosti váže, ne k
 * zakázce (jedna zakázka = jedna nemovitost, ale poptávka může být
 * evidovaná dřív, než zakázka vznikla; proto křížíme obojí).
 */
export function CaseDemandsPanel(props: {
  readonly propertyId: string | null;
  readonly caseId: string;
}): React.JSX.Element {
  const t = useT();
  const { propertyId, caseId } = props;
  const [demands, setDemands] = React.useState<readonly Demand[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const query = propertyId !== null ? { propertyId, limit: 50 } : { caseId, limit: 50 };
    listDemands(query)
      .then((result) => {
        if (!cancelled) setDemands(result.items);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof DomainApiError ? e.message : t('webAdmin.demands.loadFailed'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId, caseId, t]);

  if (error !== null) {
    return (
      <p role="alert" className="text-red-600" data-testid="case-demands-error">
        {error}
      </p>
    );
  }
  if (demands === null) return <p role="status">{t('webAdmin.demands.loading')}</p>;
  if (demands.length === 0) {
    return (
      <div data-testid="case-demands-empty">
        <p>{t('webAdmin.demands.emptyTitle')}</p>
        <p className="text-sm text-gray-600">{t('webAdmin.demands.emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div data-testid="case-demands">
      <ul className="rh-demand-drawer__timeline">
        {demands.map((demand) => {
          const summary = demandSummary(demand);
          return (
            <li key={demand.id} data-testid={`case-demand-${demand.id}`}>
              <span className="rh-badge rh-badge--neutral">
                {t(`crm.source.${demand.source}` as 'crm.source.portal')}
              </span>
              <span>
                <strong>{demand.title}</strong>
                {summary !== null ? ` · ${summary}` : ''}
                {demand.contactName !== null ? ` · ${demand.contactName}` : ''}
              </span>
              <Link to="/poptavky" className="underline" data-testid={`case-demand-link-${demand.id}`}>
                {t('webAdmin.demands.pageTitle')}
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
